// Regen slice — background regen state that survives navigation AND page refresh.
//
// When the user starts a regen from RefineView and navigates to another tab,
// the RefineView component unmounts. Without this slice, the poll loop dies
// and pending items are lost. By lifting regen state into Zustand, the
// CourseShell-level poll keeps running regardless of which sub-route is active.
//
// Refresh persistence (Issue 4 fix):
//   Active regen state (status ≠ idle) is persisted to sessionStorage on every
//   setRegenState call. On CourseShell mount, restoreRegenFromSession() checks
//   for a saved state and rehydrates the slice so the poll loop can resume.
//   resetRegen() clears the sessionStorage entry.
//
// Flow:
//   RefineView.handleStart → write to slice (status=running, runId=...)
//   CourseShell.useEffect → detects runId → polls /ingest/status → updates slice
//   RefineView mounts → reads slice → restores UI state
//   User confirms → applyPendingItems → slice.resetRegen()
//
// v4 2-phase regen (19BI pipeline alignment):
//   Phase 1 (POST /ingest/regenerate) returns { regenId, proposedItems, ... }
//   → slice status='confirm', stores pendingItems + beforeSnapshot
//   Phase 2 (POST /ingest/regenerate-confirm) applies or rejects
//   → on apply: merge items into IDB → slice.resetRegen()
//   → on reject: discard → slice.resetRegen()

import type { StateCreator } from 'zustand';
import type { PartialRegenStage } from '../../storage/idb.js';

export type RegenStatus = 'idle' | 'preparing' | 'starting' | 'running' | 'confirm' | 'done' | 'failed';

// ── Preferences (19BJ Session 6 · Regen Center Settings tab) ─────────────────
//
// Long-lived user preferences for regen behaviour. Persisted to localStorage
// (not sessionStorage — survives across sessions). Independent of the active
// regen lifecycle state.

export interface RegenPreferences {
  /** When true, regen results are auto-applied without preview.
   *  Design R7: default false (safety first). Users opt in via Settings tab. */
  autoApply: boolean;
  /** Default number of items to generate in 'add' mode.
   *  Used by ItemActionMenu (makeRegenAction count=3) and PartialRegenTab. */
  defaultAddCount: number;
}

const REGEN_PREFS_KEY = 'examforge_regen_prefs';

const DEFAULT_PREFS: RegenPreferences = {
  autoApply: false,
  defaultAddCount: 3,
};

function loadPrefs(): RegenPreferences {
  try {
    const raw = localStorage.getItem(REGEN_PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Partial<RegenPreferences>;
    return {
      autoApply: parsed.autoApply ?? DEFAULT_PREFS.autoApply,
      defaultAddCount: parsed.defaultAddCount ?? DEFAULT_PREFS.defaultAddCount,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function savePrefs(prefs: RegenPreferences): void {
  try {
    localStorage.setItem(REGEN_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable (quota, private browsing).
  }
}

// ── Phase B · Multi-job pending tracking ────────────────────────────────────
//
// Legacy sync fallback (POST /ingest/regenerate-stage) returns items
// synchronously, one job per (stage × chapter) pair. To route each job's
// items to the correct mergeStageRegenerationResult call on confirm, we
// track per-job metadata alongside the flat pendingItems list (which the
// RegenConfirmPanel renders).

export interface PendingRegenJob {
  stage: PartialRegenStage;
  chapterId?: string;
  mode: 'replace' | 'add';
  items: unknown[];
}

export interface RegenSlice {
  // ── State ──────────────────────────────────────────────────────────────────
  /** Current regen lifecycle phase. 'idle' = no regen in progress. */
  regenStatus: RegenStatus;
  /** Human-readable progress text (e.g. "正在生成… mcqs（已完成 2 阶段）"). */
  regenProgress: string;
  /** Workflow run ID — used to poll /ingest/status. Null when not running. */
  regenRunId: string | null;
  /** Items produced by regen, awaiting user confirmation before merge. */
  regenPendingItems: unknown[] | null;
  /** Stage being regenerated (e.g. 'mcqs', 'flashcards'). */
  regenTargetStage: string;
  /** Chapter ID being regenerated, null for course-wide stages. */
  regenTargetChapter: string | null;
  /** 'replace' overwrites, 'add' appends. */
  regenMode: 'replace' | 'add';
  /** Timestamp when regen started. */
  regenStartedAt: number | null;

  // ── v4 2-phase fields ──────────────────────────────────────────────────────
  /** v4 regenId (ULID) — persists across Phase 1 result + Phase 2 confirm. */
  regenId: string | null;
  /** Snapshot of items before regen (for rollback). Phase 1 response. */
  regenBeforeSnapshot: unknown[] | null;
  /** Diff summary from Phase 1 response (human-readable). */
  regenDiffSummary: string | null;

  // ── Phase B · Multi-job tracking ──────────────────────────────────────────
  /** Per-job metadata for multi-job legacy regen. Each entry maps to a
   *  (stage × chapter) pair; on confirm, items are merged per-job. */
  regenPendingJobs: PendingRegenJob[];

  // ── Progress tracking (survives navigation) ───────────────────────────────
  /** Current job index in multi-job regen (0-based). */
  regenJobCurrent: number;
  /** Total jobs in multi-job regen. */
  regenJobTotal: number;

  // ── Preferences (Settings tab · localStorage-persisted) ──────────────────
  regenPreferences: RegenPreferences;

  // ── Actions ───────────────────────────────────────────────────────────────
  /** Merge partial state into the regen slice. Persists to sessionStorage if active. */
  setRegenState: (partial: Partial<RegenSlice>) => void;
  /** Reset all regen state to initial values. Clears sessionStorage. */
  resetRegen: () => void;
  /** Restore regen state from sessionStorage (called once on CourseShell mount). */
  restoreRegenFromSession: () => void;
  /** Update a single preference key. Persists to localStorage immediately. */
  setRegenPreference: <K extends keyof RegenPreferences>(key: K, value: RegenPreferences[K]) => void;
}

const REGEN_SESSION_KEY = 'examforge_regen_state';

const initialRegenState = {
  regenStatus: 'idle' as RegenStatus,
  regenProgress: '',
  regenRunId: null as string | null,
  regenPendingItems: null as unknown[] | null,
  regenTargetStage: '',
  regenTargetChapter: null as string | null,
  regenMode: 'replace' as 'replace' | 'add',
  regenStartedAt: null as number | null,
  regenId: null as string | null,
  regenBeforeSnapshot: null as unknown[] | null,
  regenDiffSummary: null as string | null,
  regenPendingJobs: [] as PendingRegenJob[],
  regenJobCurrent: 0,
  regenJobTotal: 0,
  regenPreferences: loadPrefs(),
};

/** Fields that are safe to persist (exclude functions and large arrays). */
const PERSISTABLE_KEYS: ReadonlyArray<keyof typeof initialRegenState> = [
  'regenStatus', 'regenProgress', 'regenRunId', 'regenTargetStage',
  'regenTargetChapter', 'regenMode', 'regenStartedAt', 'regenId',
  'regenJobCurrent', 'regenJobTotal',
];

/** Statuses that indicate an active regen worth persisting. */
const ACTIVE_STATUSES: ReadonlySet<RegenStatus> = new Set([
  'preparing', 'starting', 'running', 'confirm',
]);

function persistToSession(state: Partial<RegenSlice>): void {
  try {
    const status = state.regenStatus;
    if (status && ACTIVE_STATUSES.has(status as RegenStatus)) {
      const toSave: Record<string, unknown> = {};
      for (const key of PERSISTABLE_KEYS) {
        if (key in state) {
          toSave[key] = (state as Record<string, unknown>)[key];
        }
      }
      sessionStorage.setItem(REGEN_SESSION_KEY, JSON.stringify(toSave));
    } else if (status === 'idle' || status === 'done' || status === 'failed') {
      sessionStorage.removeItem(REGEN_SESSION_KEY);
    }
  } catch {
    // sessionStorage may be unavailable (private browsing quota, etc.)
  }
}

export const createRegenSlice: StateCreator<RegenSlice> = (set, get) => ({
  ...initialRegenState,

  setRegenState: (partial) => {
    set(partial as Partial<RegenSlice>);
    persistToSession({ ...get(), ...partial });
  },
  resetRegen: () => {
    // Reset lifecycle state but preserve preferences (they are
    // localStorage-persisted and independent of the active regen lifecycle).
    const { regenPreferences } = get();
    set({ ...initialRegenState, regenPreferences });
    try { sessionStorage.removeItem(REGEN_SESSION_KEY); } catch { /* noop */ }
  },
  restoreRegenFromSession: () => {
    try {
      const raw = sessionStorage.getItem(REGEN_SESSION_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as Partial<RegenSlice>;
      if (!saved.regenStatus || !ACTIVE_STATUSES.has(saved.regenStatus as RegenStatus)) {
        // Stale entry (done/failed/idle) — clear it.
        try { sessionStorage.removeItem(REGEN_SESSION_KEY); } catch { /* noop */ }
        return;
      }
      // ── Sync-path regen (no runId): unrecoverable after refresh ──────────
      // The legacy sync path blocks on a single HTTP request. When the page
      // refreshes, the browser aborts the request and the in-flight regen
      // state is lost. There's no runId to poll, so we can't resume.
      // Graceful degradation: reset to idle so the user can re-trigger.
      const hasRunId = !!(saved.regenRunId);
      if (!hasRunId) {
        set(initialRegenState);
        try { sessionStorage.removeItem(REGEN_SESSION_KEY); } catch { /* noop */ }
        return;
      }
      // ── Async v4 workflow (has runId): resume polling ──────────────────
      set({
        regenStatus: saved.regenStatus as RegenStatus,
        regenProgress: (saved.regenProgress as string) ?? '',
        regenRunId: saved.regenRunId as string | null,
        regenTargetStage: (saved.regenTargetStage as string) ?? '',
        regenTargetChapter: (saved.regenTargetChapter as string) ?? null,
        regenMode: (saved.regenMode as 'replace' | 'add') ?? 'replace',
        regenStartedAt: (saved.regenStartedAt as number) ?? null,
        regenId: (saved.regenId as string) ?? null,
        regenJobCurrent: (saved.regenJobCurrent as number) ?? 0,
        regenJobTotal: (saved.regenJobTotal as number) ?? 0,
      });
    } catch {
      // Corrupt sessionStorage entry — clear and move on.
      try { sessionStorage.removeItem(REGEN_SESSION_KEY); } catch { /* noop */ }
    }
  },

  setRegenPreference: (key, value) => {
    const prefs = { ...get().regenPreferences, [key]: value };
    set({ regenPreferences: prefs });
    savePrefs(prefs);
  },
});
